import type React from 'react';
import { useCallback } from 'react';

import { Grid, Typography } from '@mui/material';
import { useAtomValue } from 'jotai';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { drawDefinitionConstants, entryStatusConstants, tools as factoryTools } from 'tods-competition-factory';

import {
  drawDefinitionsAtomFamily,
  eventAtomFamily,
  eventPairedDrawParticipantsAtomFamily,
  eventPairedIndividualParticipantsAtomFamily,
  eventUnpairedDrawParticipantsAtomFamily,
} from 'atoms/events';
import TMXAccordion from 'components/accordion/TMXAccordion';
import StandardPaper from 'components/papers/standard/StandardPaper';
import UnpairedParticipantsTable from 'components/tables/competitors/unpaired/UnpairedParticipantsTable';
import { TOURNAMENT_ENGINE } from 'config/graphql/constants';
import { useSaveTournament } from 'hooks/tournament/useSaveTournament';

const { MAIN } = drawDefinitionConstants;
const { ALTERNATE } = entryStatusConstants;

const DoublesCompetitorsSection: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const { t } = useTranslation();
  const event = useAtomValue(eventAtomFamily(eventId));
  const unpairedDrawParticipants = useAtomValue(eventUnpairedDrawParticipantsAtomFamily(eventId));
  const pairedDrawParticipants = useAtomValue(eventPairedDrawParticipantsAtomFamily(eventId));
  const pairedIndividualParticipants = useAtomValue(eventPairedIndividualParticipantsAtomFamily(eventId));
  const drawDefinition = useAtomValue(drawDefinitionsAtomFamily(eventId))?.[0];
  const { saveTournament } = useSaveTournament();

  const handleSaveDoublesTeams = useCallback(
    async (participantIdPairs: Array<string[]>) => {
      const method = {
        method: 'addEventEntryPairs',
        params: {
          participantIdPairs,
          eventId,
          drawId: drawDefinition?.drawId,
          uuids: factoryTools.UUIDS(participantIdPairs.length),
          entryStage: MAIN,
          entryStatus: ALTERNATE,
          allowDuplicateParticipantIdPairs: true,
        },
      };
      await saveTournament('saveDoubleTeams', TOURNAMENT_ENGINE, [method]);
    },
    [drawDefinition?.drawId, eventId, saveTournament],
  );

  return (
    <TMXAccordion
      unmountOnClose
      accordionSummaryChildren={
        <Grid container direction="column" spacing={1}>
          <Grid item>
            <Typography sx={{ marginBottom: 0 }} variant="h1">{`${t('unpaired')}`}</Typography>
          </Grid>
          <Grid item>
            <Typography variant="body2">{`${t('unpairedPlayers')} (${unpairedDrawParticipants.length})`}</Typography>
          </Grid>
        </Grid>
      }
      defaultExpanded={true}
    >
      <StandardPaper className="competitors-table">
        <UnpairedParticipantsTable
          event={event}
          pairedDrawParticipants={pairedDrawParticipants}
          pairedIndividualParticipants={pairedIndividualParticipants}
          unpairedDrawParticipants={unpairedDrawParticipants}
          onSaveTeams={handleSaveDoublesTeams}
        />
      </StandardPaper>
    </TMXAccordion>
  );
};

export default DoublesCompetitorsSection;
